import { useMutationState, useQueryClient, hashKey } from '@tanstack/react-query'
import { useCallback } from 'react'
import { replayableMutationKeyHashes } from '@/lib/offlineMutations'

interface FailedMutation {
  mutationId: number
  mutationKey: readonly unknown[] | undefined
  variables: unknown
  error: Error | null
  submittedAt: number
}

function getMutationLabel(key: readonly unknown[] | undefined, variables: unknown): string {
  if (!key) return 'Unknown change'
  const [, domain, action] = key as string[]
  const labels: Record<string, Record<string, string>> = {
    collection: {
      add: 'Add to collection',
      update: 'Update collection item',
      updateByCard: 'Update collection item',
      remove: 'Remove from collection',
      removeByCard: 'Remove from collection',
    },
    buylist: {
      add: 'Add to buylist',
      remove: 'Remove from buylist',
    },
    selllist: {
      add: 'Add to selllist',
      remove: 'Remove from selllist',
    },
    sealedCollection: {
      add: 'Add to sealed collection',
      update: 'Update sealed collection item',
      remove: 'Remove from sealed collection',
    },
  }

  const label = labels[domain]?.[action] ?? `${action} ${domain}`

  // Try to extract a card/product ID for context
  const vars = variables as Record<string, unknown> | null
  if (vars && typeof vars === 'object') {
    const id = vars.cardId ?? vars.sealedProductId ?? vars.id
    if (id) return `${label} (#${id})`
  }

  return label
}

export function useFailedMutations() {
  const queryClient = useQueryClient()

  // The FailedMutationsSheet is the offline-replay QUEUE surface — it should
  // only show mutations that are actually replayable (collection/buylist/
  // selllist/sealed writes), NOT foreground form submissions (deck create/
  // import) whose errors are surfaced inline or via toast. Without this scope a
  // keyless foreground mutation that errored leaked in as "Unknown change" with
  // a Retry that re-fails forever (#204). Resolved at call time (not module
  // scope): the set is memoized in offlineMutations.ts so this is nil-cost, and
  // it avoids a module-init side effect in a file that participates in the
  // offlineMutations eager-glob import cycle.
  const replayableKeyHashes = replayableMutationKeyHashes()

  const failedMutations = useMutationState({
    filters: {
      status: 'error',
      predicate: (mutation) => {
        const key = mutation.options.mutationKey
        return key !== undefined && replayableKeyHashes.has(hashKey(key))
      },
    },
    select: (mutation) => ({
      mutationId: mutation.mutationId,
      mutationKey: mutation.options.mutationKey,
      variables: mutation.state.variables,
      error: mutation.state.error,
      submittedAt: mutation.state.submittedAt,
    }),
  }) as FailedMutation[]

  const retryMutation = useCallback(
    (mutationId: number) => {
      const cache = queryClient.getMutationCache()
      const mutation = cache.getAll().find((m) => m.mutationId === mutationId)
      if (mutation) {
        mutation.execute(mutation.state.variables)
      }
    },
    [queryClient],
  )

  const discardMutation = useCallback(
    (mutationId: number) => {
      const cache = queryClient.getMutationCache()
      const mutation = cache.getAll().find((m) => m.mutationId === mutationId)
      if (mutation) {
        cache.remove(mutation)
      }
    },
    [queryClient],
  )

  const retryAll = useCallback(() => {
    failedMutations.forEach((m) => retryMutation(m.mutationId))
  }, [failedMutations, retryMutation])

  const discardAll = useCallback(() => {
    failedMutations.forEach((m) => discardMutation(m.mutationId))
  }, [failedMutations, discardMutation])

  return {
    failedMutations: failedMutations.map((m) => ({
      ...m,
      label: getMutationLabel(m.mutationKey, m.variables),
    })),
    retryMutation,
    discardMutation,
    retryAll,
    discardAll,
    hasFailedMutations: failedMutations.length > 0,
  }
}
